import {
  HTTPEndpointInput,
  HTTPError,
  HTTPRequestOptions,
} from '@superdispatch/http';
import { URITemplateParams } from '@superdispatch/uri';
import { useMemo } from 'react';
import { array, Schema } from 'yup';
import {
  APIErrorResponse,
  APIFailureResponse,
  APISuccessResponse,
  http,
} from './APIUtils';
import {
  CarrierPayDTO,
  carrierPaySchema,
  RecentMovedLoadDTO,
  recentMovedLoadSchema,
  RecentPostingDTO,
  recentPostingSchema,
} from './CarrierPayDTO';
import { TooManyRequestsError } from './CarrierPayErrors';
import {
  ShippingDetailsDTO,
  VenueLocationDTO,
  venueLocationSchema,
} from './ShippingDetailsDTO';

function requestJSON<
  TData,
  TParams extends URITemplateParams = URITemplateParams,
>(
  input: HTTPEndpointInput<TParams>,
  options?: HTTPRequestOptions & { schema?: Schema },
): Promise<TData> {
  return http
    .requestJSON<APISuccessResponse<TData>>(input, options)
    .then(({ data }) => {
      if (options?.schema) {
        return options.schema.cast(data);
      }
      return data;
    })
    .catch((error) => {
      if (error instanceof HTTPError) {
        if (error.response.status === 429) {
          throw new TooManyRequestsError(error.message);
        }

        return error.response
          .json()
          .then((response: APIFailureResponse | APIErrorResponse) => {
            if ('data' in response) {
              const error = new Error(response.data.message);
              throw Object.assign(error, response.data);
            }

            throw new Error(response.message);
          });
      }

      throw new Error(error.message || 'Failed to fetch');
    });
}

export function usePricingInsightsAPI() {
  return useMemo(() => {
    function getRecentPostings(payload: ShippingDetailsDTO) {
      return requestJSON<RecentPostingDTO[]>(
        'POST /internal/v1/get-recent-postings',
        {
          json: payload,
          schema: array(recentPostingSchema),
        },
      );
    }

    function getRecentMoves(payload: ShippingDetailsDTO) {
      return requestJSON<RecentMovedLoadDTO[]>(
        'POST /internal/v1/get-recent-moves',
        {
          json: payload,
          schema: array(recentMovedLoadSchema),
        },
      );
    }

    function calculateCarrierPay(payload: ShippingDetailsDTO) {
      return requestJSON<CarrierPayDTO | null>(
        'POST /internal/v1/predict-carrier-pay',
        {
          json: payload,
          schema: carrierPaySchema.nullable(),
        },
      );
    }

    function getCoordinates(payload: VenueLocationDTO) {
      return requestJSON<VenueLocationDTO>(
        'POST /internal/v1/get-coordinates',
        {
          json: payload,
          schema: venueLocationSchema,
        },
      );
    }

    return {
      getCoordinates,
      getRecentPostings,
      getRecentMoves,
      calculateCarrierPay,
    };
  }, []);
}
